import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PhoneCall, HeartHandshake, ShieldCheck, ArrowUpRight } from "lucide-react";
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { useSeo } from "@/pages/blog/useSeo";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { BookingForm } from "@/components/landing/BookingForm";
import { BookingAside } from "@/components/landing/Booking";
import { InstallmentHint } from "@/components/landing/InstallmentHint";
import { BOOK_PERIO_PATH } from "@/components/landing/data";

const POINTS = [
  { icon: PhoneCall, title: "Ti richiamiamo noi", text: "Nella fascia oraria che preferisci, per fissare il giorno insieme." },
  { icon: ShieldCheck, title: "Nessun impegno", text: "La richiesta non ti vincola a nulla: prima si parla, poi decidi." },
  { icon: HeartHandshake, title: "Se hai paura, sei nel posto giusto", text: "Andiamo al tuo ritmo. Puoi fermarti quando vuoi." },
];

const Hero = () => (
  <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-36 sm:pt-44 pb-12" data-testid="book-perio-hero">
    <p className="eyebrow text-[#3E6B48]">Prenota la visita parodontale</p>
    <h1 className="f-serif text-5xl sm:text-7xl text-[#1B3B28] mt-5 leading-[0.98] tracking-[-0.01em]" data-testid="book-perio-title">
      <MaskLine>Il primo passo,</MaskLine>
      <MaskLine delay={0.12}><em className="text-[#3E6B48]">senza fretta.</em></MaskLine>
    </h1>
    <Reveal delay={0.3}>
      <p className="text-lg text-[#526356] mt-8 max-w-2xl leading-relaxed">
        Gengive che sanguinano, alito cattivo o denti che si muovono? Lascia i tuoi dati: il Dott. Nicola Mauro ti accoglierà
        per una prima visita parodontale, con calma e senza giudizi.
      </p>
    </Reveal>
    <div className="grid sm:grid-cols-3 gap-4 mt-12">
      {POINTS.map(({ icon: Icon, title, text }, i) => (
        <Reveal key={title} delay={0.35 + i * 0.08}>
          <div className="h-full rounded-2xl border border-[#1B3B28]/10 bg-[#F4F3EC] p-5" data-testid={`book-perio-point-${i}`}>
            <span className="grid place-items-center w-10 h-10 rounded-full bg-[#86C89B]/40 text-[#1B3B28]"><Icon size={17} /></span>
            <p className="f-serif text-2xl text-[#1B3B28] mt-4 leading-tight">{title}</p>
            <p className="text-sm text-[#526356] mt-1.5 leading-snug">{text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function BookPerioPage() {
  const navigate = useNavigate();
  const [symptoms, setSymptoms] = useState([]);
  useSeo({
    title: "Prenota la prima visita parodontale",
    description: "Prenota la prima visita parodontale con il Dott. Nicola Mauro al Centro Dentistico Galliera: cura della parodontite con laser, pensata anche per i pazienti con paura del dentista.",
    path: BOOK_PERIO_PATH,
  });
  return (
    <BlogLayout>
      <div data-testid="book-perio-page">
        <Hero />
        <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pb-28 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-8" delay={0.1}>
            <div className="rounded-[2rem] bg-[#FCFBF7] border border-[#1B3B28]/10 p-7 sm:p-12 shadow-[0_30px_80px_-40px_rgba(27,59,40,0.35)]">
              <BookingForm fixedTreatment="parodontale" symptoms={symptoms} setSymptoms={setSymptoms}
                onDone={(d) => navigate("/prima-visita", { state: d })} />
            </div>
          </Reveal>
          <div className="lg:col-span-4 space-y-6">
            <Reveal delay={0.2}><BookingAside /></Reveal>
            <Reveal delay={0.25}><InstallmentHint /></Reveal>
            <Reveal delay={0.3}>
              <Link to="/igiene-gbt" data-testid="book-perio-gbt-link"
                className="group flex items-center justify-between gap-4 rounded-2xl bg-[#E6ECE4] p-5 text-[#1B3B28] hover:bg-[#D5DFD3] transition-colors">
                <span><span className="eyebrow text-[#3E6B48] block">Cerchi una pulizia dei denti?</span><span className="f-serif text-xl">Scopri l'igiene GBT</span></span>
                <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:rotate-45" />
              </Link>
            </Reveal>
          </div>
        </section>
      </div>
    </BlogLayout>
  );
}
